type PriceRow = Record<string, string | number>
export type PriceMatrix = Record<string, PriceRow>

/** Parse fence / element price matrix from API (JSON string or object). */
export function parsePriceMatrix (raw: unknown): PriceMatrix {
  if (raw == null || raw === '') {
    return {}
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as PriceMatrix
  }
  if (typeof raw === 'string') {
    const csvLike = raw.includes(';') && !raw.includes('{') && !raw.includes('[')
    if (csvLike) {
      const rows = raw
        .split(/\r?\n/)
        .map((r) => r.trim())
        .filter((r) => r.length > 0)
      if (rows.length >= 2) {
        const headerCells = rows[0].split(';').map((c) => c.trim())
        const columns = headerCells.slice(1).filter(Boolean)
        const out: PriceMatrix = {}

        for (const row of rows.slice(1)) {
          const cells = row.split(';').map((c) => c.trim())
          if (!cells.length || !cells[0]) continue
          const lengthKey = cells[0].replace(',', '.')
          const priceRow: PriceRow = {}

          columns.forEach((col, idx) => {
            const rawValue = cells[idx + 1]
            if (rawValue == null || rawValue === '') return
            const normalized = String(rawValue).replace(',', '.')
            const numeric = parseFloat(normalized)
            priceRow[col] = Number.isFinite(numeric) ? numeric : normalized
          })

          out[lengthKey] = priceRow
        }
        return out
      }
    }
    try {
      const parsed = JSON.parse(raw) as PriceMatrix
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  return {}
}

/** Sorted length keys (numeric sort, fixed two decimals to match matrix keys). */
export function getSortedLengths (matrix: PriceMatrix): string[] {
  return Object.keys(matrix)
    .map((k) => parseFloat(k))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b)
    .map((n) => n.toFixed(2))
}

/** Length shown in the configurator input (whole numbers without decimal places). */
export function formatFenceLengthDisplay (value: string | number | null | undefined): string {
  if (value == null || value === '') {
    return ''
  }

  const n = typeof value === 'number'
    ? value
    : parseFloat(String(value).replace(',', '.'))

  if (!Number.isFinite(n)) {
    return String(value)
  }

  if (Number.isInteger(n)) {
    return String(n)
  }

  return n.toFixed(2).replace(/\.?0+$/, '')
}

const COLOR_OPTION_TITLE = /zaunfarbe/i

function parseLocalizedFloat (value: unknown): number {
  if (value == null || value === '') return 0
  const parsed = parseFloat(typeof value === 'number' ? String(value) : String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

function lengthNumbers (matrix: PriceMatrix): number[] {
  return getSortedLengths(matrix)
    .map((key) => parseFloat(key))
    .filter((n) => Number.isFinite(n))
}

function snapToMatrixLength (length: number, lengths: number[], fallbackLength: number): number {
  if (!lengths.length) return 0
  if (length === 0) return fallbackLength || lengths[0]
  const next = lengths.find((step) => step >= length)
  return next != null ? next : lengths[lengths.length - 1]
}

function matrixRow (matrix: PriceMatrix, length: number): PriceRow | undefined {
  const fixed = length.toFixed(2)
  const compact = String(parseFloat(fixed))
  return matrix[fixed] || matrix[compact] || matrix[String(length)]
}

function defaultColorSku (product: { custom_options?: any[] }, row: PriceRow | undefined): string {
  const options = Array.isArray(product.custom_options) ? product.custom_options : []
  const selects = options.filter((option) => option && (option.type === 'select' || option.type === 'drop_down'))
  const colorOption = selects.find((option) => COLOR_OPTION_TITLE.test(String(option.title || ''))) || selects[0]
  const values = colorOption && Array.isArray(colorOption.values) ? colorOption.values : []
  const sku = values[0] && values[0].sku != null ? String(values[0].sku) : ''
  if (sku && row && row[sku] != null && row[sku] !== '') return sku
  const fallback = row ? Object.keys(row).find((key) => row[key] != null && row[key] !== '') : ''
  return fallback || ''
}

/**
 * Price the fence configurator shows before the visitor changes length or color.
 * Returns null when the product has no price matrix.
 */
export function defaultFenceOfferPrice (product: {
  price_matrix_fence?: unknown
  price_matrix_elements?: unknown
  fence_default_length?: unknown
  fence_width?: unknown
  custom_options?: any[]
} | null | undefined): number | null {
  if (!product) return null
  const matrix = parsePriceMatrix(product.price_matrix_fence)
  const lengths = lengthNumbers(matrix)
  if (!lengths.length) return null

  const fallbackLength = product.fence_default_length
    ? parseLocalizedFloat(product.fence_default_length)
    : lengths[0]
  const requested = product.fence_default_length
    ? parseLocalizedFloat(product.fence_default_length)
    : lengths[0]
  const fenceWidth = parseInt(String(product.fence_width), 10)
  let length = requested
  if (Number.isFinite(fenceWidth) && fenceWidth > 0 && length % fenceWidth !== 0) {
    length = Math.ceil(length / fenceWidth) * fenceWidth
  }
  const matrixLength = snapToMatrixLength(length, lengths, fallbackLength || lengths[0])
  const row = matrixRow(matrix, matrixLength)
  const colorSku = defaultColorSku(product, row)
  const base = row && colorSku ? parseLocalizedFloat(row[colorSku]) : 0
  if (!(base > 0)) return null

  let step = 1
  if (lengths.length >= 2 && lengths[1] - lengths[0] > 0) {
    step = lengths[1] - lengths[0]
  } else if (Number.isFinite(fenceWidth) && fenceWidth > 0) {
    step = fenceWidth
  }
  const extra = requested - matrixLength
  const additional = extra > 0 ? Math.ceil(extra / step) * step : 0
  let price = base
  if (additional > 0) {
    const elements = parsePriceMatrix(product.price_matrix_elements)
    const elementRow = Object.keys(elements).map((key) => elements[key]).find((entry) => !!entry)
    const elementPrice = elementRow ? parseLocalizedFloat(elementRow[colorSku]) : 0
    price += (elementPrice * additional) / step
  }
  return Math.round(price * 100) / 100
}

/** Positive catalog price, or the default fence configuration when the catalog price is empty. */
export function schemaOfferPrice (product: any): number | null {
  const fencePrice = defaultFenceOfferPrice(product)
  if (fencePrice != null) return fencePrice
  const candidates = product
    ? [product.price_incl_tax, product.priceInclTax, product.final_price, product.price]
    : []
  for (const value of candidates) {
    const parsed = parseLocalizedFloat(value)
    if (parsed > 0) return parsed
  }
  return null
}
